import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';

import { BuyerService } from '../../../core/api/buyer.service';
import { RoleEnum } from '../../../shared/constants/role.enums';
import { Buyer } from '../../../shared/models';
import * as fromApp from '../../../store';

@Component({
  selector: 'fc-edit-buyer-page',
  templateUrl: './edit-buyer-page.component.html',
  styleUrls: ['./edit-buyer-page.component.scss'],
})
export class EditBuyerPageComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  @ViewChild('welcomeDocFileUpload', { static: false })
  welcomeDocFileUpload: ElementRef;

  id: number = null;
  buyer: Buyer = null;
  buyerForm: UntypedFormGroup;
  error: string = null;
  isSuperAdmin = false;
  welcomeDocs: any[];

  constructor(
    readonly buyerService: BuyerService,
    private formBuilder: UntypedFormBuilder,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly store: Store<fromApp.AppState>
  ) {
    this.createEditBuyerForm();
  }

  ngOnInit() {
    this.id = +this.route.snapshot.params.id;

    this.store
      .select('auth', 'currentUser')
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.isSuperAdmin = currentUser.hasRole(RoleEnum.SuperAdmin);
        if (!this.isSuperAdmin) {
          this.buyerForm.disable();
        }
      });

    forkJoin([this.buyerService.getBuyerById(this.id), this.buyerService.listWelcomeDocs(this.id)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([buyer, welcomeDocs]: [Buyer, any[]]) => {
        this.buyerForm.get('id').setValue(buyer.id);
        this.buyerForm.get('referrer_code').setValue(buyer.referrerCode);
        this.buyerForm.get('name').setValue(buyer.name);
        this.buyerForm.get('gtag_id').setValue(buyer.gtagId);
        this.buyerForm.get('short_name').setValue(buyer.shortName);
        this.buyerForm.get('contact_address_line_1').setValue(buyer.contactAddressLine1);
        this.buyerForm.get('contact_address_line_2').setValue(buyer.contactAddressLine2);
        this.buyerForm.get('contact_city').setValue(buyer.contactCity);
        this.buyerForm.get('contact_state').setValue(buyer.contactState);
        this.buyerForm.get('contact_zip').setValue(buyer.contactZip);
        this.buyerForm.get('secondary_signer_name').setValue(buyer.secondarySignerName);
        this.buyerForm.get('secondary_signer_title').setValue(buyer.secondarySignerTitle);
        this.buyerForm.get('secondary_signer_email').setValue(buyer.secondarySignerEmail);
        this.buyerForm.get('docusign_integrator_key').setValue(buyer.docusignIntegratorKey);
        this.buyerForm.get('docusign_user_id').setValue(buyer.docusignUserId);
        this.buyerForm.get('docusign_private_key').setValue(buyer.docusignPrivateKey);
        this.buyerForm.get('trulioo_username').setValue(buyer.truliooUsername);
        this.buyerForm.get('trulioo_password').setValue(buyer.truliooPassword);
        this.buyerForm.get('slogan').setValue(buyer.slogan);
        this.buyerForm.get('stripe_secret_key').setValue(buyer.stripeSecretKey);
        this.buyerForm.get('stripe_public_key').setValue(buyer.stripePublicKey);
        this.buyerForm.get('stripe_client_id').setValue(buyer.stripeClientId);
        this.buyerForm.get('buyer_signature_settings').setValue(buyer.buyerSignatureSettings);
        this.buyerForm.get('landowner_signature_settings').setValue(buyer.landownerSignatureSettings);
        this.buyerForm.get('landowner_title_settings').setValue(buyer.landownerTitleSettings);
        this.buyerForm.get('landowner_entity_settings').setValue(buyer.landownerEntitySettings);
        this.buyerForm.get('url').setValue(buyer.url);
        this.welcomeDocs = welcomeDocs;
      });
  }

  createEditBuyerForm(): void {
    this.buyerForm = this.formBuilder.group({
      id: '',
      name: ['', Validators.required],
      referrer_code: '',
      gtag_id: '',
      short_name: '',
      contact_address_line_1: '',
      contact_address_line_2: '',
      contact_city: '',
      contact_state: '',
      contact_zip: '',
      secondary_signer_name: '',
      secondary_signer_title: '',
      secondary_signer_email: '',
      docusign_integrator_key: '',
      docusign_user_id: '',
      docusign_private_key: '',
      trulioo_username: '',
      trulioo_password: '',
      slogan: '',
      stripe_secret_key: '',
      stripe_public_key: '',
      stripe_client_id: '',
      buyer_signature_settings: '',
      landowner_signature_settings: '',
      landowner_title_settings: '',
      landowner_entity_settings: '',
      url: '',
    });
  }

  onSubmit(): void {
    const buyer: Buyer = new Buyer({
      id: this.buyerForm.value.id,
      name: this.buyerForm.value.name,
      referrerCode: this.buyerForm.value.referrer_code,
      gtagId: this.buyerForm.value.gtag_id,
      shortName: this.buyerForm.value.short_name,
      contactAddressLine1: this.buyerForm.value.contact_address_line_1,
      contactAddressLine2: this.buyerForm.value.contact_address_line_2,
      contactCity: this.buyerForm.value.contact_city,
      contactState: this.buyerForm.value.contact_state,
      contactZip: this.buyerForm.value.contact_zip,
      secondarySignerName: this.buyerForm.value.secondary_signer_name,
      secondarySignerTitle: this.buyerForm.value.secondary_signer_title,
      secondarySignerEmail: this.buyerForm.value.secondary_signer_email,
      docusignIntegratorKey: this.buyerForm.value.docusign_integrator_key,
      docusignUserId: this.buyerForm.value.docusign_user_id,
      docusignPrivateKey: this.buyerForm.value.docusign_private_key,
      truliooUsername: this.buyerForm.value.trulioo_username,
      truliooPassword: this.buyerForm.value.trulioo_password,
      slogan: this.buyerForm.value.slogan,
      stripeSecretKey: this.buyerForm.value.stripe_secret_key,
      stripePublicKey: this.buyerForm.value.stripe_public_key,
      stripeClientId: this.buyerForm.value.stripe_client_id,
      buyerSignatureSettings: this.buyerForm.value.buyer_signature_settings,
      landownerSignatureSettings: this.buyerForm.value.landowner_signature_settings,
      landownerTitleSettings: this.buyerForm.value.landowner_title_settings,
      landownerEntitySettings: this.buyerForm.value.landowner_entity_settings,
      url: this.buyerForm.value.url,
    });
    this.buyerService.saveBuyer(buyer).subscribe(() => this.router.navigateByUrl(`/admin/manage-buyers`));
  }

  uploadWelcomeDoc($event: any) {
    const welcomeDocFileUpload = this.welcomeDocFileUpload.nativeElement;
    welcomeDocFileUpload.onchange = () => {
      if (welcomeDocFileUpload.files.length === 1) {
        const file = welcomeDocFileUpload.files[0];
        this.buyerService.uploadWelcomeDoc(this.id, file).then((newDoc: any) => {
          this.welcomeDocs.push(newDoc);
        });
        this.welcomeDocFileUpload.nativeElement.value = '';
      }
    };
    welcomeDocFileUpload.click();
  }

  removeWelcomeDoc($event: MouseEvent, docToDelete: any) {
    const docKey = `${docToDelete['uuid']}-${docToDelete['name']}`;
    this.buyerService.deleteWelcomeDoc(this.id, docKey).subscribe((deleted: boolean) => {
      if (deleted) {
        const idx = this.welcomeDocs.findIndex(
          (welcomeDoc: any) => `${welcomeDoc['uuid']}-${welcomeDoc['name']}` === docKey
        );
        if (idx > -1) {
          this.welcomeDocs.splice(idx, 1);
        }
      }
    });
  }
}
